import { useEffect, useState, type KeyboardEvent } from 'react';
import { marginBp } from '@app/shared/price-calc';
import { api, ApiError, fieldIssues } from '../api';
import { formatPln, parsePln, plnToInput } from '../catalog/money';
import { NumberInput } from '../ui/NumberInput';
import { formatDecimal } from '../ui/numbers';
import type { QuoteLine } from './types';
import { useRowGuard } from '../ui/unsaved';

/**
 * Owner 2026-10-24 10:08: why a line's cost is an estimate (its tooltip) — the missing pieces not rented yet, at the
 * suppliers' average day price (whose, when the role sees them) or at my price when no supplier has the item.
 */
export function estimateTitle(line: Pick<QuoteLine, 'estimate' | 'estimatedCostGrosze' | 'rentalCostGrosze'>): string | undefined {
  const e = line.estimate;
  const amount = line.estimatedCostGrosze ?? 0;
  if (!e || amount <= 0) return undefined;
  const why =
    e.source === 'suppliers'
      ? `średnia cen dostawców za dobę${e.suppliers && e.suppliers.length > 0 ? ` (${e.suppliers.map((s) => `${s.supplierName} ${formatPln(s.priceDayGrosze)}`).join(', ')})` : ''} = ${formatPln(e.unitPriceGrosze)}`
      : `wg Twojej ceny (${formatPln(e.unitPriceGrosze)} za dobę), bo żaden dostawca nie ma tej pozycji`;
  const rented = (line.rentalCostGrosze ?? 0) > 0 ? ` W koszcie są też dopożyczenia: ${formatPln(line.rentalCostGrosze!)}.` : '';
  return `Koszt szacunkowy: brakuje ${e.missing} szt., jeszcze ich nie dopożyczono — ${why} × ${e.missing} szt. × dni pozycji = ${formatPln(amount)}. Dopożycz, a koszt będzie prawdziwy.${rented}`;
}

/** 6000 bp -> "60%", 7333 -> "73,3%". */
export const marginText = (bp: number): string => `${formatDecimal(Math.round(bp / 10) / 10)}%`;

/**
 * F29 (owner 09:23): the company's cost of a line — only for whoever may see it (the column is not rendered
 * otherwise). People's services start from their function's rate; any cost can be typed (negotiated), also in a
 * sent or accepted version (the client never sees it). Enter or leaving the field saves; an emptied field goes back
 * to the rate. No "are you sure".
 */
export function CostCell({ line, editable, onSaved, onConflict }: { line: QuoteLine; editable: boolean; onSaved: (line: QuoteLine) => void; onConflict: () => void }) {
  const cost = line.costGrosze ?? null;
  // owner 22:24: the event's rentals of the item are in the line's cost; so is the estimate of a shortage not rented yet
  const rented = line.rentalCostGrosze ?? 0;
  const estimated = line.estimatedCostGrosze ?? 0;
  // owner 2026-10-24 („nie zlicza się koszt przy dopożyczeniu”, „koszt sumuje się i pokazuje tak jak było wcześniej”):
  // the field shows the line's whole cost; what is typed above the REAL rentals is the line's own cost (review: never
  // against the estimate — a typed cost replaces the estimate, the server then leaves it out)
  const extras = rented;
  const shown = cost === null ? '' : plnToInput(cost);
  const [text, setText] = useState(shown);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => setText(shown), [shown, line.version]);
  const net = line.netGrosze;
  // 2026-10-24: own transport's cost and how it was counted (fuel × consumption + wear per km × km + fixed per event, owner 16:15),
  // the same in „Ryczałt” and „Wg km” (owner 10:22)
  const basisTitle = line.transport && line.costBasis ? `Koszt: ${line.costBasis}. Wpisz własny koszt, jeśli inny.` : undefined;
  const margin = cost !== null && net !== undefined ? marginBp(net - cost, net) : null;
  // owner 2026-10-24 10:08: a cost holding an estimate is red, the reason on hover
  const estimateWhy = estimateTitle(line);

  /** Saves a typed cost; false when it did not (the reason is shown). */
  async function persist(): Promise<boolean> {
    if (saving || text.trim() === shown) return true;
    let costGrosze: number | null;
    if (text.trim() === '') costGrosze = null;
    else {
      try {
        costGrosze = parsePln(text);
      } catch (e) {
        setError((e as Error).message);
        return false;
      }
    }
    if (costGrosze !== null && (extras > 0 || estimated > 0)) {
      if (costGrosze < extras) {
        setError(`Koszt nie może być mniejszy niż koszt dopożyczeń (${formatPln(extras)}). Zmień dopożyczenie w Dopożyczeniach.`);
        return false;
      }
      // nothing above the rentals: no own cost
      // nothing above the rentals: no own cost — unless an estimate is there, which the typed cost replaces (own 0)
      costGrosze = costGrosze === extras && estimated === 0 ? null : costGrosze - extras;
    }
    if (costGrosze === null && !line.costOverridden) {
      setText(shown);
      return true;
    }
    setSaving(true);
    try {
      onSaved(await api<QuoteLine>(`/api/quotes/lines/${line.id}/cost`, { method: 'PATCH', body: { costGrosze, version: line.version } }));
      setError('');
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError('Ktoś w tym czasie zmienił tę pozycję. Pokazuję aktualne wartości.');
        onConflict();
      } else setError(e instanceof ApiError ? (fieldIssues(e)[0]?.message ?? e.message) : 'Nie udało się zapisać kosztu');
      return false;
    } finally {
      setSaving(false);
    }
  }
  // owner 2026-10-23 21:14: a cost typed and not saved (leaving the field saves): leaving the card or tab waits or asks
  const row = useRowGuard(editable && !line.rentalLocked && text.trim() !== shown, persist);
  const save = row.save;
  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    // the row's own Enter / Escape (saving the line) must not see this field
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setText(shown);
      setError('');
    }
  }

  return (
    <td ref={row.ref} className="num q-cost" data-label="Koszt" data-feature-id="quotes.costs">
      {editable && !line.rentalLocked ? (
        <NumberInput
          className={`cell-input num${estimateWhy ? ' q-cost-estimated' : ''}`}
          kind="money"
          aria-label={`Koszt: ${line.name}`}
          placeholder={line.transport ? 'brak km' : '—'}
          title={
            estimateWhy ??
            (extras > 0
              ? `Koszt pozycji razem z dopożyczeniami (${formatPln(extras)}). Wpisz wyższy, jeśli masz własny koszt ponad dopożyczenia.`
              : line.costOverridden
                ? 'Koszt wpisany ręcznie. Wyczyść pole, by wrócić do stawki.'
                : (basisTitle ?? 'Koszt dla firmy (ze stawki). Wpisz własny, jeśli wynegocjowany.'))
          }
          value={text}
          onValueChange={setText}
          onKeyDown={onKeyDown}
          onBlur={(e) => {
            e.stopPropagation();
            void save();
          }}
        />
      ) : (
        <span className={estimateWhy ? 'q-cost-estimated' : undefined} title={estimateWhy ?? (line.costOverridden ? undefined : basisTitle)}>
          {cost === null ? '—' : formatPln(cost)}
        </span>
      )}
      {(margin !== null || line.costOverridden || line.costFromSupplier) && (
        <span className="q-cost-note">
          {line.costOverridden && 'wpisany'}
          {line.costFromSupplier && `cena: ${line.costSupplier ?? 'dostawca'}`}
          {(line.costOverridden || line.costFromSupplier) && margin !== null && ' · '}
          {margin !== null && `marża ${marginText(margin)}`}
        </span>
      )}
      {line.rentalLocked && <span className="q-cost-note hint">koszt zmieniasz w Dopożyczeniach (sprzęt już odebrany)</span>}
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </td>
  );
}
